import { DefaultContainer } from '@/components/DefaultContainer';
import { ErrorComponent } from '@/components/ErrorComponent';
import { ArrowBack, ContentCopy, Save } from '@mui/icons-material';
import { Grid, IconButton, Paper, Stack, Tooltip } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  EditorDialog,
  ExerciseDescriptionSection,
  ExerciseEquipmentSection,
  ExerciseFactsSection,
  ExerciseImagesSection,
  ExerciseInfoSection,
  ExerciseLinksSection,
  ExerciseMediaSection,
  ExerciseMusclesSection,
  ExerciseProgressionsSection,
  ExercisePrerequisitesSection,
  ExerciseRegressionsSection,
  ExerciseSupportExercisesSection,
  ExerciseTitleSection,
  ExerciseTrainingSection,
} from './editor/ExerciseEditorSections';
import { TextScanModal } from './modals/TextScanModal';
import { ExercisePrerequisiteModal } from './modals/ExercisePrerequisiteModal';
import { DiscardExerciseChangesModal } from './modals/DiscardExerciseChangesModal';
import { SupportExerciseModal } from './modals/SupportExerciseModal';
import { CopySupportExercisesModal } from './modals/CopySupportExercisesModal';
import { CopyPrerequisitesModal } from './modals/CopyPrerequisitesModal';
import { CopyMusclesModal } from './modals/CopyMusclesModal';
import { allExerciseDataIds, CopyExerciseDataModal, difficultyCopyId } from './modals/CopyExerciseDataModal';
import { SelectExercisesDrawer } from './SelectExerciseDrawer';
import { useExerciseEditor } from './useExerciseEditor';
import { AppContext } from '@/contexts/app.context';
import { ExercisePrerequisiteDTO, SupportExerciseDTO } from '@/generated';
import { useAllExercisesQuery } from '@/hooks/query-hooks';

export const UpdateExercisePage = () => {
  const { exerciseId } = useParams();
  return <ExerciseEditor key={exerciseId ?? 'new'} exerciseId={exerciseId} />;
};

const ExerciseEditor = (props: { exerciseId?: string }) => {
  const { exerciseId } = props;
  const editor = useExerciseEditor(exerciseId);
  const context = useContext(AppContext);
  const { exercise, updateExercise, validationErrors, hasUnsavedChanges, isEditMode, isSaving, save } = editor;
  const [activeDialog, setActiveDialog] = useState<EditorDialog>(null);
  const [selectedPrerequisite, setSelectedPrerequisite] = useState<ExercisePrerequisiteDTO>();
  const [selectedSupportExercise, setSelectedSupportExercise] = useState<SupportExerciseDTO>();
  const { exercises = [] } = useAllExercisesQuery(true);
  const navigate = useNavigate();

  useEffect(() => {
    context.setExtraTitle('Create Exercise');
    if (isEditMode) context.setExtraTitle('Edit Exercise');
  }, [isEditMode, context]);

  if (!exercise) return null;

  return (
    <DefaultContainer>
      <SelectExercisesDrawer
        show={activeDialog === 'progression' || activeDialog === 'regression' || activeDialog === 'support' || activeDialog === 'prerequisite'}
        setShow={(show) => {
          if (!show) setActiveDialog(null);
        }}
        exerciseSelected={(selected) => {
          if (activeDialog === 'prerequisite') {
            const existing = exercise.prerequisites.find((value) => value.exerciseId === selected.id);
            setSelectedPrerequisite(existing ?? { exerciseId: selected.id });
            setActiveDialog('prerequisiteDetails');
            return;
          }
          if (activeDialog === 'support') {
            const existing = exercise.supportExercises.find((value) => value.exerciseId === selected.id);
            setSelectedSupportExercise(existing ?? { exerciseId: selected.id });
            setActiveDialog('supportDetails');
            return;
          }
          if (activeDialog !== 'progression' && activeDialog !== 'regression') return;
          const field = activeDialog === 'progression' ? 'progressionExerciseIds' : 'regressionExerciseIds';
          if (!exercise[field].includes(selected.id)) updateExercise({ [field]: [...exercise[field], selected.id] });
          setActiveDialog(null);
        }}
      />
      <TextScanModal
        show={activeDialog === 'textScan'}
        onHide={(text: string) => {
          if (text) updateExercise({ description: text });
          setActiveDialog(null);
        }}
        setShow={() => setActiveDialog(null)}
      />
      <Paper sx={{ my: 1, zIndex: 1, p: 1, position: 'sticky', top: { xs: 60, sm: 65 } }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Tooltip title="Back to exercises">
            <IconButton
              onClick={() => {
                if (hasUnsavedChanges) setActiveDialog('discard');
                else navigate(-1);
              }}
              aria-label="Back to exercises"
            >
              <ArrowBack />
            </IconButton>
          </Tooltip>
          <Tooltip title={isEditMode ? 'Save exercise' : 'Create exercise'}>
            <IconButton
              aria-label={isEditMode ? 'Save exercise' : 'Create exercise'}
              disabled={isSaving}
              onClick={async () => {
                if (await save()) {
                  enqueueSnackbar({ message: `${exercise.name} saved!`, variant: 'success' });
                  navigate(-1);
                }
              }}
            >
              <Save />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy exercise data from a progression or regression">
            <IconButton aria-label="Copy exercise data" onClick={() => setActiveDialog('copyAll')}>
              <ContentCopy />
            </IconButton>
          </Tooltip>
          {/* <Typography variant="subtitle1">{isEditMode ? 'Edit exercise' : 'Create exercise'}</Typography> */}
        </Stack>
      </Paper>
      <ErrorComponent errors={validationErrors} />
      <Paper elevation={4} sx={{ width: '100%', p: { xs: 2, md: 3 } }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={2}>
              <ExerciseMediaSection editor={editor} exercise={exercise} />
              <ExerciseInfoSection editor={editor} exercise={exercise} openDialog={setActiveDialog} />
              <ExerciseMusclesSection editor={editor} exercise={exercise} openDialog={setActiveDialog} />
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <Stack spacing={2}>
              <ExerciseTitleSection editor={editor} exercise={exercise} />
              <ExerciseDescriptionSection editor={editor} openDialog={setActiveDialog} exercise={exercise} />
              <ExerciseTrainingSection editor={editor} exercise={exercise} openDialog={setActiveDialog} />
              <ExerciseLinksSection editor={editor} exercise={exercise} openDialog={setActiveDialog} />
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <ExerciseFactsSection editor={editor} exercise={exercise} openDialog={setActiveDialog} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <ExerciseImagesSection editor={editor} exercise={exercise} />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <ExerciseEquipmentSection editor={editor} exercise={exercise} openDialog={setActiveDialog} />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <ExerciseProgressionsSection editor={editor} openDialog={setActiveDialog} exercise={exercise} />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <ExerciseRegressionsSection editor={editor} openDialog={setActiveDialog} exercise={exercise} />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <ExerciseSupportExercisesSection
              editor={editor}
              openDialog={setActiveDialog}
              exercise={exercise}
              editSupportExercise={(value) => {
                setSelectedSupportExercise({ ...value });
                setActiveDialog('supportDetails');
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <ExercisePrerequisitesSection
              editor={editor}
              openDialog={setActiveDialog}
              exercise={exercise}
              editPrerequisite={(value) => {
                setSelectedPrerequisite({ ...value });
                setActiveDialog('prerequisiteDetails');
              }}
            />
          </Grid>
        </Grid>
      </Paper>
      <ExercisePrerequisiteModal
        show={activeDialog === 'prerequisiteDetails'}
        prerequisite={selectedPrerequisite}
        onHide={() => {
          setActiveDialog(null);
          setSelectedPrerequisite(undefined);
        }}
        onSave={(prerequisite) => {
          const prerequisites = exercise.prerequisites.filter((value) => value.exerciseId !== prerequisite.exerciseId);
          updateExercise({ prerequisites: [...prerequisites, prerequisite] });
          setActiveDialog(null);
          setSelectedPrerequisite(undefined);
        }}
      />
      <SupportExerciseModal
        show={activeDialog === 'supportDetails'}
        supportExercise={selectedSupportExercise}
        onHide={() => {
          setActiveDialog(null);
          setSelectedSupportExercise(undefined);
        }}
        onSave={(supportExercise) => {
          const supportExercises = exercise.supportExercises.filter((value) => value.exerciseId !== supportExercise.exerciseId);
          updateExercise({ supportExercises: [...supportExercises, supportExercise] });
          setActiveDialog(null);
          setSelectedSupportExercise(undefined);
        }}
      />
      <CopySupportExercisesModal
        show={activeDialog === 'copySupport'}
        progressionExerciseIds={exercise.progressionExerciseIds}
        regressionExerciseIds={exercise.regressionExerciseIds}
        exercises={exercises}
        onHide={() => setActiveDialog(null)}
        onCopy={(copiedSupportExercises) => {
          const existingIds = new Set(exercise.supportExercises.map((supportExercise) => supportExercise.exerciseId));
          updateExercise({
            supportExercises: [
              ...exercise.supportExercises,
              ...copiedSupportExercises.filter((supportExercise) => !existingIds.has(supportExercise.exerciseId)).map((supportExercise) => ({ ...supportExercise })),
            ],
          });
          setActiveDialog(null);
        }}
      />
      <CopyPrerequisitesModal
        show={activeDialog === 'copyPrerequisites'}
        progressionExerciseIds={exercise.progressionExerciseIds}
        regressionExerciseIds={exercise.regressionExerciseIds}
        exercises={exercises}
        onHide={() => setActiveDialog(null)}
        onCopy={(copiedPrerequisites) => {
          const existingIds = new Set(exercise.prerequisites.map((prerequisite) => prerequisite.exerciseId));
          updateExercise({
            prerequisites: [
              ...exercise.prerequisites,
              ...copiedPrerequisites.filter((prerequisite) => !existingIds.has(prerequisite.exerciseId)).map((prerequisite) => ({ ...prerequisite })),
            ],
          });
          setActiveDialog(null);
        }}
      />
      <CopyMusclesModal
        show={activeDialog === 'copyMuscles'}
        progressionExerciseIds={exercise.progressionExerciseIds}
        regressionExerciseIds={exercise.regressionExerciseIds}
        exercises={exercises}
        onHide={() => setActiveDialog(null)}
        onCopy={(values) => {
          updateExercise(values);
          setActiveDialog(null);
        }}
      />
      <CopyExerciseDataModal
        show={activeDialog === 'copyEquipment'}
        mode="equipment"
        progressionExerciseIds={exercise.progressionExerciseIds}
        regressionExerciseIds={exercise.regressionExerciseIds}
        exercises={exercises}
        onHide={() => setActiveDialog(null)}
        onCopy={(sourceExercise, selectedIds) => {
          const existingIds = new Set(exercise.equipment.map((item) => item.equipmentId));
          updateExercise({
            equipment: [
              ...exercise.equipment,
              ...sourceExercise.equipment.filter((item) => selectedIds.includes(item.equipmentId) && !existingIds.has(item.equipmentId)).map((item) => ({ ...item })),
            ],
          });
          setActiveDialog(null);
        }}
      />
      <CopyExerciseDataModal
        show={activeDialog === 'copyCategories'}
        mode="categories"
        progressionExerciseIds={exercise.progressionExerciseIds}
        regressionExerciseIds={exercise.regressionExerciseIds}
        exercises={exercises}
        onHide={() => setActiveDialog(null)}
        onCopy={(sourceExercise, selectedIds) => {
          updateExercise({
            categoryIds: [...new Set([...exercise.categoryIds, ...sourceExercise.categoryIds.filter((id) => selectedIds.includes(id))])],
            ...(selectedIds.includes(difficultyCopyId) && { difficulty: sourceExercise.difficulty }),
          });
          setActiveDialog(null);
        }}
      />
      <CopyExerciseDataModal
        show={activeDialog === 'copyFacts'}
        mode="facts"
        progressionExerciseIds={exercise.progressionExerciseIds}
        regressionExerciseIds={exercise.regressionExerciseIds}
        exercises={exercises}
        onHide={() => setActiveDialog(null)}
        onCopy={(sourceExercise, selectedIds) => {
          updateExercise({ exerciseFactIds: [...new Set([...(exercise.exerciseFactIds ?? []), ...(sourceExercise.exerciseFactIds ?? []).filter((id) => selectedIds.includes(id))])] });
          setActiveDialog(null);
        }}
      />
      <CopyExerciseDataModal
        show={activeDialog === 'copyTraining'}
        mode="training"
        progressionExerciseIds={exercise.progressionExerciseIds}
        regressionExerciseIds={exercise.regressionExerciseIds}
        exercises={exercises}
        onHide={() => setActiveDialog(null)}
        onCopy={(sourceExercise) => {
          updateExercise({
            config: {
              ...sourceExercise.config,
              timing: sourceExercise.config.timing
                ? { ...sourceExercise.config.timing, intervals: sourceExercise.config.timing.intervals.map((interval) => ({ ...interval })) }
                : undefined,
            },
          });
          setActiveDialog(null);
        }}
      />
      <CopyExerciseDataModal
        show={activeDialog === 'copyLinks'}
        mode="links"
        progressionExerciseIds={exercise.progressionExerciseIds}
        regressionExerciseIds={exercise.regressionExerciseIds}
        exercises={exercises}
        onHide={() => setActiveDialog(null)}
        onCopy={(sourceExercise, selectedIds) => {
          const selectedLinks = sourceExercise.links.filter((_, index) => selectedIds.includes(String(index)));
          const existingLinks = new Set(exercise.links.map((link) => `${link.title}\u0000${link.uri}`));
          updateExercise({ links: [...exercise.links, ...selectedLinks.filter((link) => !existingLinks.has(`${link.title}\u0000${link.uri}`)).map((link) => ({ ...link }))] });
          setActiveDialog(null);
        }}
      />
      <CopyExerciseDataModal
        show={activeDialog === 'copyAll'}
        mode="all"
        progressionExerciseIds={exercise.progressionExerciseIds}
        regressionExerciseIds={exercise.regressionExerciseIds}
        exercises={exercises}
        onHide={() => setActiveDialog(null)}
        onCopy={(sourceExercise, selectedIds) => {
          const selected = new Set(selectedIds as (typeof allExerciseDataIds)[number][]);
          updateExercise({
            ...(selected.has('equipment') && { equipment: sourceExercise.equipment.map((item) => ({ ...item })) }),
            ...(selected.has('prerequisites') && { prerequisites: sourceExercise.prerequisites.map((item) => ({ ...item })) }),
            ...(selected.has('supportExercises') && { supportExercises: sourceExercise.supportExercises.map((item) => ({ ...item })) }),
            ...(selected.has('facts') && { exerciseFactIds: [...(sourceExercise.exerciseFactIds ?? [])] }),
            ...(selected.has('links') && { links: sourceExercise.links.map((item) => ({ ...item })) }),
            ...(selected.has('info') && { categoryIds: [...sourceExercise.categoryIds], difficulty: sourceExercise.difficulty }),
            ...(selected.has('muscles') && {
              mainBodyZoneId: sourceExercise.mainBodyZoneId,
              muscleIntensities: sourceExercise.muscleIntensities.map((item) => ({ ...item })),
            }),
            ...(selected.has('training') && {
              config: {
                ...sourceExercise.config,
                timing: sourceExercise.config.timing
                  ? { ...sourceExercise.config.timing, intervals: sourceExercise.config.timing.intervals.map((interval) => ({ ...interval })) }
                  : undefined,
              },
            }),
          });
          setActiveDialog(null);
        }}
      />
      <DiscardExerciseChangesModal show={activeDialog === 'discard'} onHide={() => setActiveDialog(null)} onDiscard={() => navigate(-1)} />
    </DefaultContainer>
  );
};
